/** One location the demo reset can act on: it has at least one ACTIVE agent. */
export interface ResetLocation {
  locationId: string;
  locationName: string;
  agentNames: string[];
}

interface ResetRowLike {
  locationId: string;
  locationName: string;
  agentName: string;
  assignment: { status: string } | null;
}

/** Locations with an active agent, in first-seen row order, each listing its active agents. */
export function resetLocations(rows: ReadonlyArray<ResetRowLike>): ResetLocation[] {
  const byId = new Map<string, ResetLocation>();
  for (const row of rows) {
    if (row.assignment?.status !== 'active') continue;
    const existing = byId.get(row.locationId);
    if (existing) existing.agentNames.push(row.agentName);
    else byId.set(row.locationId, { locationId: row.locationId, locationName: row.locationName, agentNames: [row.agentName] });
  }
  return [...byId.values()];
}

/** Same rule as the backend: the location name, ignoring case and outer spaces. */
export function resetConfirmMatches(typed: string, locationName: string): boolean {
  const want = locationName.trim().toLowerCase();
  return want.length > 0 && typed.trim().toLowerCase() === want;
}

const GENERIC_RESET_ERROR = 'Could not reset the agents. Try again.';
const IN_PROGRESS_ERROR = 'An agent run is in progress for this location — try again in a few minutes.';

/**
 * Turn a caught reset request error into the line shown inside the confirm dialog. Duck-types the
 * status code instead of importing the API client's error class, so this stays a plain, dependency-free
 * util that `test:sam`'s restricted tsconfig can compile on its own.
 */
export function resetErrorMessage(err: unknown): string {
  const statusCode = typeof (err as { statusCode?: unknown })?.statusCode === 'number'
    ? (err as { statusCode: number }).statusCode
    : undefined;
  const message = err instanceof Error && err.message ? err.message : undefined;
  if (statusCode === 409) return message ?? IN_PROGRESS_ERROR;
  return message ?? GENERIC_RESET_ERROR;
}
